import { requestWork } from '@/lib/work-actions';
import { toDashboardUiError } from '@/lib/dashboard-errors';
import { useOptionalAppAssistant } from '@/lib/app-assistant-context';
import type { WorkActionContext } from '@shared/types/work-action';
import { supabase } from '@/lib/supabase';
import { ACCOUNT_TASK_COMPLETED_EVENT } from './AccountCapabilityLauncher';
import { useEffect, useRef, useState } from 'react';
import type {
  ActivitySubjectChatTurn,
} from '@shared/types/activity-feed';
import type { LightViewAction } from '@shared/types/views';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { getSubjectChatHistory } from '@/lib/activity-feed';
import { streamSubjectChat } from '@/lib/activity-feed-stream';
import { useStickyChatScroll } from '@/lib/hooks/useStickyChatScroll';
import { cn } from '@/lib/utils';

interface SubjectComposerProps {
  continueFromResult?: boolean;
  workFocus?: WorkActionContext;
  subjectKey: string | null;
  subjectKind: string;
  subjectLabel: string;
}

interface ComposerMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  action: LightViewAction | null;
}

const MAX_HISTORY_TURNS = 12;

export const SubjectComposer = ({
  continueFromResult = false,
  subjectKey,
  subjectKind,
  subjectLabel,
  workFocus,
}: SubjectComposerProps) => {
  const assistant = useOptionalAppAssistant();
  const [messages, setMessages] = useState<ComposerMessage[]>([]);
  const [input, setInput] = useState(workFocus?.jobId || workFocus?.resultId || workFocus?.flowResultId || workFocus?.preparation ? '' : workFocus?.title ?? '');
  const [pending, setPending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const counterRef = useRef(0);
  const prepareRequest = useRef<{ key: string; id: string } | null>(null);
  const preparing = useRef(false);
  const storageKey = useRef<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(workFocus));
  const evaluating = workFocus?.preparation?.status === 'queued' || workFocus?.preparation?.status === 'running';
  useEffect(() => {
    if (!workFocus) return;
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active || !data.session) return;
      storageKey.current = `domino:work-draft:${data.session.user.id}:${workFocus.subjectId}:${workFocus.flowResultId ? `flow:${workFocus.flowResultId}` : workFocus.topicId ?? 'new'}`;
      const saved = sessionStorage.getItem(storageKey.current);
      if (saved) {
        const draft = JSON.parse(saved) as { text?: unknown; request?: { key: string; id: string } };
        if (typeof draft.text === 'string') setInput(draft.text);
        if (typeof draft.request?.id === 'string' && typeof draft.request.key === 'string') prepareRequest.current = draft.request;
      }
    }).catch(() => { if (active) setError('Le brouillon local n’a pas pu être retrouvé.'); })
      .finally(() => { if (active) setLoadingDraft(false); });
    return () => { active = false; };
  }, [workFocus?.subjectId, workFocus?.topicId, workFocus?.flowResultId]);
  useEffect(() => {
    if (!continueFromResult || input !== workFocus?.title) return;
    setInput('');
  }, [continueFromResult, input, workFocus?.title]);
  const saveDraft = (text: string) => {
    if (!storageKey.current) return;
    try { sessionStorage.setItem(storageKey.current, JSON.stringify({ text, request: prepareRequest.current })); }
    catch { setError('La saisie ne peut pas être conservée dans ce navigateur. Gardez ce panneau ouvert.'); }
  };
  const {
    scrollRef: messagesRef,
    handleScroll: handleMessagesScroll,
    resumeAutoScroll,
  } = useStickyChatScroll<HTMLDivElement>({
    watch: [messages, pending],
  });

  const nextId = (): string => {
    counterRef.current += 1;
    return `msg-${counterRef.current}`;
  };

  // Load the persisted thread when the drawer opens on a subject (and reset on
  // subject change). A stale subject's response never overwrites the new one.
  useEffect(() => {
    let cancelled = false;
    setMessages([]);
    setError(null);
    setLoadingHistory(true);

    getSubjectChatHistory({ subjectKey, subjectKind, subjectLabel, subjectId: workFocus?.subjectId, topicId: workFocus?.topicId, flowResultId: workFocus?.flowResultId })
      .then((response) => {
        if (cancelled) return;
        setMessages(response.messages.map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          action: message.action,
        })));
      })
      .catch(() => { if (!cancelled) setError('La conversation précédente n’a pas pu être chargée.'); })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [subjectKey, subjectKind, subjectLabel, workFocus?.subjectId, workFocus?.topicId, workFocus?.flowResultId]);

  const handleSend = async (): Promise<void> => {
    const text = input.trim();
    if (!text || pending) return;

    resumeAutoScroll();
    const userMessage: ComposerMessage = { id: nextId(), role: 'user', content: text, action: null };
    const history: ActivitySubjectChatTurn[] = [...messages, userMessage]
      .slice(-MAX_HISTORY_TURNS)
      .map((message) => ({ role: message.role, content: message.content }));

    // Pre-insert an empty assistant message that fills in as deltas stream.
    const assistantId = nextId();
    setMessages((prev) => [
      ...prev,
      userMessage,
      { id: assistantId, role: 'assistant', content: '', action: null },
    ]);
    setInput('');
    setPending(true);
    setError(null);

    try {
      const response = await streamSubjectChat(
        {
          subjectId: workFocus?.subjectId,
          topicId: workFocus?.topicId,
          flowResultId: workFocus?.flowResultId,
          subjectKey,
          subjectKind,
          subjectLabel,
          message: text,
          // Drop the just-added user turn from history; it is sent as `message`.
          history: history.slice(0, -1),
        },
        {
          onReplyDelta: (delta) => {
            setMessages((prev) => prev.map((message) => (
              message.id === assistantId
                ? { ...message, content: message.content + delta }
                : message
            )));
          },
        },
      );

      // Reconcile with the validated final reply + any proposed action.
      setMessages((prev) => prev.map((message) => (
        message.id === assistantId
          ? { ...message, content: response.reply, action: response.proposedAction }
          : message
      )));
    } catch (caught) {
      setMessages((prev) => prev.filter((message) => (
        message.id !== userMessage.id && message.id !== assistantId
      )));
      setInput(text);
      saveDraft(text);
      setError((await toDashboardUiError(caught, 'Impossible de contacter l’assistant.')).message);
    } finally {
      setPending(false);
    }
  };

  const prepareWork = async () => {
    if (!workFocus || workFocus.flowResultId || pending || evaluating || preparing.current || !input.trim()) return;
    preparing.current = true;
    const request = input.trim(); setPending(true); setError(null);
    const key = JSON.stringify([workFocus.subjectId, workFocus.topicId, request]);
    if (prepareRequest.current?.key !== key) prepareRequest.current = { key, id: crypto.randomUUID() };
    saveDraft(input);
    try {
      const requestId = prepareRequest.current.id;
      const prepared = await requestWork(workFocus, request, requestId);
      prepareRequest.current = null;
      setInput(''); saveDraft('');
      if (!prepared.reused) setMessages(prev => [...prev.filter(m => m.id !== requestId && m.id !== `${requestId}:accepted`),
        { id: requestId, role: 'user', content: request, action: null },
        { id: `${requestId}:accepted`, role: 'assistant', content: prepared.reply ?? 'Demande enregistrée.', action: null }]);
      assistant?.openWork({ ...workFocus, ...prepared, preparation: { jobId: prepared.jobId, status: prepared.status === 'running' ? 'running' : prepared.status === 'completed' ? 'completed' : 'queued', message: null, failureCode: null } });
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'La préparation n’a pas abouti.'); }
    finally { preparing.current = false; setPending(false); window.dispatchEvent(new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT)); }
  };

  return (
    <section aria-label="Votre demande" className="space-y-3">
      {loadingHistory && messages.length === 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          Chargement de la conversation…
        </div>
      )}

      {messages.length > 0 && (
        <details open={!workFocus || pending}>
          <summary className="cursor-pointer py-2 text-xs text-domino-inkSoft">Échanges précédents</summary>
        <div
          ref={messagesRef}
          className="mt-4 max-h-80 space-y-3 overflow-y-auto pr-1"
          onScroll={handleMessagesScroll}
        >
          {messages.filter(message => !message.id.endsWith(':accepted')).map((message) => (
            <div
              key={message.id}
              className={cn(
                'text-sm leading-6',
                message.role === 'user'
                  ? 'rounded-2xl bg-domino-muted px-4 py-3 text-gray-800'
                  : 'px-1 py-1 text-gray-900'
              )}
            >
              {message.role === 'user' ? (
                <p className="whitespace-pre-wrap">{message.content}</p>
              ) : (
                <ChatMarkdownRenderer content={message.content} />
              )}
              {message.action && (
                <div className="mt-3">
                  <ViewActionButton action={message.action} />
                </div>
              )}
            </div>
          ))}
        </div>
        </details>
      )}

      {error && (
        <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
      )}

      <div className="mt-4 space-y-2">
        <Textarea
          value={input}
          onChange={(event) => { setInput(event.target.value); saveDraft(event.target.value); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void (workFocus && !continueFromResult ? prepareWork() : handleSend());
            }
          }}
          maxLength={workFocus ? 500 : 2000}
          aria-label="Votre demande pour ce compte"
          rows={3}
          placeholder={workFocus?.flowResultId ? 'Posez une question sur ce résultat…' : continueFromResult
            ? 'Dites à Domino ce que vous retenez ou souhaitez faire ensuite…'
            : workFocus?.topicId
              ? 'Décrivez ce qui doit changer dans ce travail…'
              : 'Décrivez le travail à préparer…'}
          disabled={pending || loadingHistory || loadingDraft || evaluating}
        />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <p className="text-[11px] text-gray-400">{workFocus && !continueFromResult ? '⌘/Ctrl + Entrée pour préparer' : '⌘/Ctrl + Entrée pour envoyer'}</p>
          {workFocus && continueFromResult ? (
            <>
              <Button onClick={() => void handleSend()} disabled={pending || loadingHistory || loadingDraft || evaluating || input.trim().length === 0} loading={pending}>Envoyer à Domino</Button>
              {!workFocus.flowResultId && <Button variant="ghost" disabled={pending || loadingHistory || loadingDraft || evaluating || !input.trim()} onClick={() => void prepareWork()}>Modifier ce travail</Button>}
            </>
          ) : workFocus ? (
            <>
              <Button disabled={pending || loadingHistory || loadingDraft || evaluating || !input.trim()} onClick={() => void prepareWork()}>{evaluating ? 'Préparation en cours…' : 'Préparer ce travail'}</Button>
              <Button variant="ghost" onClick={() => void handleSend()} disabled={pending || loadingHistory || loadingDraft || evaluating || input.trim().length === 0} loading={pending}>Poser une question</Button>
            </>
          ) : (
            <Button onClick={() => void handleSend()} disabled={pending || loadingHistory || loadingDraft || evaluating || input.trim().length === 0} loading={pending}>Envoyer</Button>
          )}
        </div>
      </div>
    </section>
  );
};
